import { motion } from 'framer-motion';
import { ArrowLeft, BadgeCheck, Code2, KeyRound, Terminal, Trophy, UserCog } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { NOMINAL_CONTEST_DURATION_LABEL, powerUpRules, TEAM_QUESTION_COUNT, timeRules } from '../lib/arena-rules';
import { errorMessage } from '../lib/callables';
import { formatFaNumber } from '../lib/format';
import { useAuth } from '../providers/AuthProvider';

const queryLines = [
  ['۰۱', 'challenge', ': فروش هر محصول را رتبه‌بندی کنید'],
  ['۰۲', 'SELECT', ' TOP (3) p.Name, SUM(d.LineTotal) AS Revenue'],
  ['۰۳', 'FROM', ' SalesOrderDetail d'],
  ['۰۴', 'JOIN', ' Product p ON p.ProductID = d.ProductID'],
  ['۰۵', 'GROUP BY', ' p.Name'],
  ['۰۶', 'ORDER BY', ' Revenue DESC;'],
] as const;

type LoginMode = 'team' | 'admin';

export function LoginPage() {
  const { loginWithTeamCode, loginAsAdmin } = useAuth();
  const [mode, setMode] = useState<LoginMode>('team');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (code.trim().length < 6) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'admin') await loginAsAdmin(code);
      else await loginWithTeamCode(code);
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-ink text-white">
      <div className="arena-grid data-noise absolute inset-0" />
      <div className="absolute -left-36 top-[-15%] size-[560px] rounded-full bg-brand/15 blur-[120px]" />
      <div className="absolute -bottom-52 right-[20%] size-[480px] rounded-full bg-plum/20 blur-[130px]" />

      <header className="relative z-10 mx-auto flex h-24 max-w-[1400px] items-center justify-between px-7 lg:px-12">
        <BrandLockup inverse />
        <div className="flex items-center gap-2 text-xs font-medium text-white/55">
          <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_12px_#34d399]" />
          مسابقه آماده شروع است
        </div>
      </header>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-6rem)] max-w-[1400px] items-center gap-14 px-7 pb-16 lg:grid-cols-[.85fr_1.15fr] lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-xl"
        >
          <p className="mb-5 flex items-center gap-3 text-sm font-bold text-brand">
            <span className="h-px w-10 bg-brand" />
            DataBinex T-SQL Arena
          </p>
          <h1 className="text-5xl font-black leading-[1.16] tracking-tight md:text-7xl">
            وارد میدان شوید.
            <br />
            <span className="text-brand">Query</span> بنویسید.
            <br />
            با زمان رقابت کنید.
          </h1>
          <p className="mt-7 max-w-lg text-base leading-8 text-slate-300 md:text-lg">
            هر تیم با کد مسابقه وارد می‌شود، سؤال T-SQL می‌گیرد و باید Query کامل بنویسد. امتیاز از ترکیب دقت Query و سرعت پاسخ محاسبه می‌شود.
          </p>
          <div className="mt-7 max-w-xl border-y border-white/10 py-5">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/55">
              <b className="text-white">مسیر {formatFaNumber(TEAM_QUESTION_COUNT)} سؤالی</b>
              <span>زمان اسمی کل {NOMINAL_CONTEST_DURATION_LABEL} دقیقه</span>
              {timeRules.map((item) => <span key={item.label}>{item.label}: {item.value}</span>)}
            </div>
            <p className="mt-3 text-[11px] text-white/45">جایزه‌ها: راهنما، ۳۰ ثانیه اضافه و فرصت دوباره؛ توضیح کامل بعد از ورود نمایش داده می‌شود.</p>
            <div className="mt-4 hidden gap-2 text-[11px] leading-5 text-white/45 2xl:grid 2xl:grid-cols-3">
              {powerUpRules.map((item) => (
                <p key={item.title}><b className="text-brand">{item.title}</b> · {item.detail}</p>
              ))}
            </div>
          </div>

          <form onSubmit={(event) => void handleLogin(event)} className="mt-9 max-w-md">
            <div className="mb-3 grid grid-cols-2 rounded-xl border border-white/10 bg-white/[.04] p-1">
              <button type="button" onClick={() => setMode('team')} className={`flex h-10 items-center justify-center gap-2 rounded-lg text-xs font-black transition ${mode === 'team' ? 'bg-brand text-white' : 'text-white/55 hover:text-white'}`}>
                <KeyRound className="size-4" />
                تیم
              </button>
              <button type="button" onClick={() => setMode('admin')} className={`flex h-10 items-center justify-center gap-2 rounded-lg text-xs font-black transition ${mode === 'admin' ? 'bg-white text-ink' : 'text-white/55 hover:text-white'}`}>
                <UserCog className="size-4" />
                مدیر
              </button>
            </div>
            <label className="block">
              <span className="mb-2 block text-xs font-bold text-white/55">{mode === 'admin' ? 'کد مدیر مسابقه' : 'کد تیم'}</span>
              <Input
                dir="ltr"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder={mode === 'admin' ? 'DBX-ADMIN-7429' : 'DBX-1234-ABCD-EF'}
                className="h-14 border-white/10 bg-white text-center font-mono text-lg font-black tracking-[0.2em] text-ink"
                autoComplete="one-time-code"
              />
            </label>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <Button size="lg" disabled={busy || code.trim().length < 6} type="submit">
                {busy ? 'در حال بررسی…' : mode === 'admin' ? 'رفتن به اتاق کنترل' : 'ورود به میدان'}
                <ArrowLeft className="size-4" />
              </Button>
              <span className="flex items-center gap-2 text-xs text-white/50"><BadgeCheck className="size-4" /> کد تیم شما را به همان جای قبلی مسابقه برمی‌گرداند</span>
            </div>
            {error && <p role="alert" className="mt-4 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-200">{error}</p>}
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotateY: -5 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative hidden lg:block"
        >
          <div className="absolute -inset-10 rounded-full bg-brand/10 blur-3xl" />
          <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0A0F1A]/88 shadow-2xl backdrop-blur">
            <div className="flex h-14 items-center justify-between border-b border-white/8 px-5" dir="ltr">
              <div className="flex gap-2"><i className="size-2.5 rounded-full bg-red-400" /><i className="size-2.5 rounded-full bg-amber-400" /><i className="size-2.5 rounded-full bg-emerald-400" /></div>
              <span className="font-mono text-[11px] text-white/35">arena-round.sql</span>
              <Terminal className="size-4 text-brand" />
            </div>
            <div className="relative px-5 py-8 font-mono text-[13px] leading-9" dir="ltr">
              <motion.div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-brand/8 to-transparent" animate={{ y: [0, 250, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'linear' }} />
              {queryLines.map(([line, keyword, rest], index) => (
                <motion.div key={line} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + index * 0.08 }} className="grid grid-cols-[28px_1fr]">
                  <span className="select-none text-white/18">{line}</span>
                  <span><b className="font-medium text-brand">{keyword}</b><span className="text-slate-300">{rest}</span></span>
                </motion.div>
              ))}
            </div>
            <div className="flex h-16 items-center justify-between border-t border-white/8 bg-white/[.025] px-5">
              <div className="flex items-center gap-5 text-xs text-white/45" dir="ltr"><span>T-SQL Query Mode</span><span>score = accuracy × time</span></div>
              <div className="flex items-center gap-2 rounded-lg bg-emerald-400/12 px-3 py-2 text-xs font-bold text-emerald-300"><Code2 className="size-3.5" /> Query کامل ثبت شد</div>
            </div>
          </div>
          <div className="absolute -bottom-7 -right-7 flex items-center gap-3 rounded-2xl border border-white/10 bg-navy/90 px-4 py-3 shadow-xl backdrop-blur">
            <Trophy className="size-5 text-brand" />
            <div><b className="block text-sm">قانون بازی</b><span className="text-[10px] text-white/45">فقط خروجی نهایی امتیاز ندارد</span></div>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
